/**
 * Минимальный маршрутизатор.
 *
 * Полноценная библиотека здесь избыточна: страниц немного, вложенных
 * маршрутов нет, а нужны только адресная строка, кнопка «назад» и прямые
 * ссылки вида /events/12. Всё это даёт History API.
 */

import { useCallback, useEffect, useState, type MouseEvent } from 'react';

import { DEFAULT_SETTINGS_TAB, isSettingsTab, type SettingsTab } from './settings-tabs.ts';

export function navigate(to: string): void {
  if (to === window.location.pathname + window.location.search) {
    return;
  }

  window.history.pushState({}, '', to);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function useRoute(): string {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const update = (): void => {
      setPath(window.location.pathname);
    };

    window.addEventListener('popstate', update);
    return () => {
      window.removeEventListener('popstate', update);
    };
  }, []);

  return path;
}

export interface RouteMatch {
  page:
    | 'overview'
    | 'players'
    | 'player'
    | 'events'
    | 'event'
    | 'applications'
    | 'rollbacks'
    | 'stats'
    | 'capture'
    | 'static'
    | 'tactics'
    | 'broadcast'
    | 'audit'
    | 'settings'
    | 'setup'
    | 'unknown';
  eventId?: number;
  userId?: string;
  captureId?: number;
  staticId?: string;
  settingsTab?: SettingsTab;
}

export function matchRoute(path: string): RouteMatch {
  if (path === '/' || path === '') {
    return { page: 'overview' };
  }

  if (path === '/tactics') {
    return { page: 'tactics' };
  }

  const eventMatch = /^\/events\/(\d+)$/.exec(path);
  if (eventMatch?.[1] !== undefined) {
    return { page: 'event', eventId: Number.parseInt(eventMatch[1], 10) };
  }

  // Идентификатор Discord — длинное число, но остаётся строкой: в числе
  // такой длины теряются младшие разряды, и ссылка вела бы не к тому человеку.
  const playerMatch = /^\/players\/(\d+)$/.exec(path);
  if (playerMatch?.[1] !== undefined) {
    return { page: 'player', userId: playerMatch[1] };
  }

  const captureMatch = /^\/stats\/captures\/(\d+)$/.exec(path);
  if (captureMatch?.[1] !== undefined) {
    return { page: 'capture', captureId: Number.parseInt(captureMatch[1], 10) };
  }

  // Статик — не число: в нём встречаются ведущие нули, и разбор в число
  // превратил бы «077» в «77», то есть в чужого человека.
  const staticMatch = /^\/stats\/statics\/([^/]+)$/.exec(path);
  if (staticMatch?.[1] !== undefined) {
    return { page: 'static', staticId: decodeURIComponent(staticMatch[1]) };
  }

  // Незнакомая вкладка настроек — это опечатка в адресе, а не настройки:
  // показывать по ней пустую страницу значило бы делать вид, что раздел есть.
  const settingsMatch = /^\/settings\/([^/]+)$/.exec(path);
  if (settingsMatch?.[1] !== undefined) {
    const slug = decodeURIComponent(settingsMatch[1]);
    return isSettingsTab(slug) ? { page: 'settings', settingsTab: slug } : { page: 'unknown' };
  }

  switch (path) {
    case '/players':
      return { page: 'players' };
    case '/events':
      return { page: 'events' };
    case '/applications':
      return { page: 'applications' };
    case '/rollbacks':
      return { page: 'rollbacks' };
    case '/stats':
      return { page: 'stats' };
    case '/broadcast':
      return { page: 'broadcast' };
    case '/audit':
      return { page: 'audit' };
    case '/settings':
      return { page: 'settings', settingsTab: DEFAULT_SETTINGS_TAB };
    case '/setup':
      return { page: 'setup' };
    default:
      return { page: 'unknown' };
  }
}

/** Обработчик для ссылок внутри приложения. */
/** Из чего складывается решение «перехватывать ли клик». */
export interface LinkClickFacts {
  /** Кто-то уже занялся этим кликом. */
  defaultPrevented: boolean;
  /** Клик с модификатором или не левой кнопкой: открывают в новой вкладке. */
  modified: boolean;
  /** Клик пришёл из кнопки или поля, лежащего внутри ссылки. */
  fromControl: boolean;
  href: string | null;
}

/**
 * Перехватывать ли клик по ссылке своим маршрутизатором.
 *
 * Отдельной функцией, потому что правил уже четыре, а проверять их в
 * браузере — значит ловить переход, которого быть не должно.
 *
 * Про `fromControl` стоит сказать отдельно. Строка игрока в статистике —
 * целиком ссылка на карточку, а внутри неё живут кнопки: переключатель тира,
 * а завтра что-нибудь ещё. Без этого правила нажатие на кнопку уводило со
 * страницы вместо того, чтобы сделать своё дело. Каждая такая кнопка может
 * гасить всплытие сама, но забыть об этом легко, а поймать — только руками.
 */
export function shouldHandleLinkClick(facts: LinkClickFacts): boolean {
  if (facts.defaultPrevented || facts.modified || facts.fromControl) {
    return false;
  }

  return facts.href !== null && facts.href.startsWith('/');
}

/** Что внутри ссылки считается самостоятельным управлением. */
const CONTROLS = 'button, input, select, textarea, [role="button"]';

export function useLinkHandler(): (event: MouseEvent<HTMLAnchorElement>) => void {
  return useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    const target = event.target instanceof Element ? event.target : null;

    const facts: LinkClickFacts = {
      defaultPrevented: event.defaultPrevented,
      modified: event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0,
      fromControl: target !== null && target.closest(CONTROLS) !== null,
      href: event.currentTarget.getAttribute('href'),
    };

    if (!shouldHandleLinkClick(facts)) {
      return;
    }

    event.preventDefault();
    navigate(facts.href ?? '/');
  }, []);
}
